import { Link } from "react-router-dom";
import { Calendar, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Calendar className="h-8 w-8 text-primary" strokeWidth={1.5} />
              <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                PrenotaFacile
              </span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Torna alla Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Informativa sulla Privacy
          </h1>
          <p className="text-slate-500 mb-8">Ultimo aggiornamento: Gennaio 2026</p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">1. Titolare del Trattamento</h2>
              <p className="text-slate-600 leading-relaxed">
                Il Titolare del trattamento dei dati personali raccolti attraverso la piattaforma PrenotaFacile 
                è la società che gestisce il servizio. Per qualsiasi informazione o richiesta relativa al 
                trattamento dei dati personali, è possibile contattarci all'indirizzo email: 
                <a href="mailto:privacy@prenotafacile.cloud" className="text-primary hover:underline ml-1">
                  privacy@prenotafacile.cloud
                </a>
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">2. Dati Raccolti</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                PrenotaFacile raccoglie e tratta le seguenti categorie di dati personali:
              </p>
              <div className="bg-slate-50 rounded-lg p-6 space-y-4">
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">Dati di Registrazione</h3>
                  <ul className="list-disc list-inside text-slate-600 space-y-1">
                    <li>Nome e cognome</li>
                    <li>Indirizzo email</li>
                    <li>Password (criptata)</li>
                    <li>Nome dell'attività commerciale</li>
                    <li>Numero di telefono (opzionale)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">Dati di Prenotazione</h3>
                  <ul className="list-disc list-inside text-slate-600 space-y-1">
                    <li>Nome del cliente</li>
                    <li>Email del cliente</li>
                    <li>Numero di telefono del cliente</li>
                    <li>Data e ora della prenotazione</li>
                    <li>Servizi richiesti</li>
                    <li>Note aggiuntive</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">Dati di Pagamento</h3>
                  <ul className="list-disc list-inside text-slate-600 space-y-1">
                    <li>Dati della transazione (gestiti da Stripe)</li>
                    <li>Storico degli abbonamenti</li>
                    <li>Fatturazione</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">Dati Tecnici</h3>
                  <ul className="list-disc list-inside text-slate-600 space-y-1">
                    <li>Indirizzo IP</li>
                    <li>Tipo di browser e dispositivo</li>
                    <li>Cookie e tecnologie simili</li>
                    <li>Log di accesso</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">3. Finalità del Trattamento</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                I dati personali sono trattati per le seguenti finalità:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li><strong>Erogazione del servizio:</strong> gestione account, prenotazioni, calendari e comunicazioni con i clienti</li>
                <li><strong>Gestione pagamenti:</strong> elaborazione abbonamenti e transazioni tramite Stripe</li>
                <li><strong>Comunicazioni di servizio:</strong> notifiche email relative a prenotazioni, promemoria e aggiornamenti</li>
                <li><strong>Assistenza clienti:</strong> risposta a richieste di supporto</li>
                <li><strong>Miglioramento del servizio:</strong> analisi aggregate e anonime per migliorare la piattaforma</li>
                <li><strong>Adempimenti legali:</strong> rispetto di obblighi di legge e richieste delle autorità</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">4. Base Giuridica</h2>
              <p className="text-slate-600 leading-relaxed">
                Il trattamento dei dati personali si fonda sulle seguenti basi giuridiche ai sensi del 
                Regolamento UE 2016/679 (GDPR):
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 mt-4">
                <li><strong>Esecuzione del contratto (Art. 6.1.b):</strong> per l'erogazione dei servizi richiesti</li>
                <li><strong>Consenso (Art. 6.1.a):</strong> per comunicazioni promozionali e marketing</li>
                <li><strong>Interesse legittimo (Art. 6.1.f):</strong> per migliorare la sicurezza e prevenire frodi</li>
                <li><strong>Obbligo legale (Art. 6.1.c):</strong> per adempiere a obblighi di legge</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Condivisione dei Dati</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                I dati personali possono essere condivisi con:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li><strong>Stripe:</strong> per la gestione dei pagamenti (conforme a PCI-DSS)</li>
                <li><strong>Google:</strong> per la sincronizzazione con Google Calendar (se abilitata)</li>
                <li><strong>Cloudinary:</strong> per lo storage delle immagini</li>
                <li><strong>Provider di hosting:</strong> per l'infrastruttura tecnologica</li>
                <li><strong>Autorità competenti:</strong> in caso di richieste legittime</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                Non vendiamo né cediamo i tuoi dati personali a terzi per finalità di marketing.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">6. Conservazione dei Dati</h2>
              <p className="text-slate-600 leading-relaxed">
                I dati personali sono conservati per il tempo necessario alle finalità per cui sono stati raccolti:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 mt-4">
                <li><strong>Dati dell'account:</strong> fino alla cancellazione dell'account + 30 giorni</li>
                <li><strong>Dati di prenotazione:</strong> 3 anni dalla data della prenotazione</li>
                <li><strong>Dati di fatturazione:</strong> 10 anni (obbligo fiscale)</li>
                <li><strong>Log tecnici:</strong> 12 mesi</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">7. I Tuoi Diritti</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Ai sensi del GDPR, hai diritto di:
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-medium text-slate-900 mb-2">Accesso</h3>
                  <p className="text-sm text-slate-600">Ottenere conferma e copia dei tuoi dati personali</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-medium text-slate-900 mb-2">Rettifica</h3>
                  <p className="text-sm text-slate-600">Correggere dati inesatti o incompleti</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-medium text-slate-900 mb-2">Cancellazione</h3>
                  <p className="text-sm text-slate-600">Richiedere la cancellazione dei tuoi dati</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-medium text-slate-900 mb-2">Portabilità</h3>
                  <p className="text-sm text-slate-600">Ricevere i tuoi dati in formato strutturato</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-medium text-slate-900 mb-2">Limitazione</h3>
                  <p className="text-sm text-slate-600">Limitare il trattamento in determinati casi</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-medium text-slate-900 mb-2">Opposizione</h3>
                  <p className="text-sm text-slate-600">Opporti al trattamento per motivi legittimi</p>
                </div>
              </div>
              <p className="text-slate-600 leading-relaxed mt-4">
                Per esercitare questi diritti, contattaci a: 
                <a href="mailto:privacy@prenotafacile.cloud" className="text-primary hover:underline ml-1">
                  privacy@prenotafacile.cloud
                </a>
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">8. Cookie</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Utilizziamo cookie e tecnologie simili per:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li><strong>Cookie tecnici:</strong> necessari per il funzionamento del sito</li>
                <li><strong>Cookie di sessione:</strong> per mantenere l'autenticazione</li>
                <li><strong>Cookie analitici:</strong> per analisi aggregate del traffico (anonimizzati)</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                Puoi gestire le preferenze sui cookie attraverso le impostazioni del tuo browser.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">9. Sicurezza</h2>
              <p className="text-slate-600 leading-relaxed">
                Adottiamo misure tecniche e organizzative per proteggere i tuoi dati:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 mt-4">
                <li>Crittografia SSL/TLS per tutte le comunicazioni</li>
                <li>Password criptate con algoritmi sicuri</li>
                <li>Accesso ai dati limitato al personale autorizzato</li>
                <li>Backup regolari e disaster recovery</li>
                <li>Monitoraggio continuo della sicurezza</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">10. Modifiche alla Privacy Policy</h2>
              <p className="text-slate-600 leading-relaxed">
                Ci riserviamo il diritto di modificare questa informativa. Le modifiche saranno pubblicate 
                su questa pagina con la data di aggiornamento. Ti consigliamo di consultare periodicamente 
                questa pagina. L'uso continuato del servizio dopo le modifiche costituisce accettazione 
                delle stesse.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">11. Contatti</h2>
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                <p className="text-slate-600 leading-relaxed">
                  Per qualsiasi domanda relativa a questa informativa o al trattamento dei tuoi dati:
                </p>
                <ul className="mt-4 space-y-2 text-slate-600">
                  <li>
                    <strong>Email:</strong>{" "}
                    <a href="mailto:privacy@prenotafacile.cloud" className="text-primary hover:underline">
                      privacy@prenotafacile.cloud
                    </a>
                  </li>
                  <li>
                    <strong>Reclami:</strong> Hai diritto di proporre reclamo al Garante per la Protezione 
                    dei Dati Personali (www.garanteprivacy.it)
                  </li>
                </ul>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <Calendar className="h-6 w-6 text-primary" strokeWidth={1.5} />
              <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                PrenotaFacile
              </span>
            </Link>
            <div className="flex gap-6 text-sm text-slate-500">
              <Link to="/privacy" className="hover:text-slate-900">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-slate-900">Termini di Servizio</Link>
            </div>
            <p className="text-sm text-slate-500">
              © 2025 PrenotaFacile. Tutti i diritti riservati.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
